import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components/auth/AuthLayout';
import { AuthCard } from '../components/auth/AuthCard';
import { Button } from '../components/shared/Button';
import { CheckCircle, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export const VerifyEmailPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token?.trim()) {
      setStatus('error');
      setMessage(t('auth.verifyEmail.messages.invalidLink'));
      return;
    }

    setStatus('loading');
    const url = `${API_BASE}/auth/verify-email?token=${encodeURIComponent(token)}`;
    fetch(url)
      .then((res) => {
        if (res.ok) return res.json();
        return res.json().then((body) => Promise.reject(body));
      })
      .then(() => {
        setStatus('success');
        setMessage(t('auth.verifyEmail.messages.success'));
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err?.message || t('auth.verifyEmail.messages.failed'));
      });
  }, [t, token]);

  return (
    <AuthLayout
      variant="register"
      title={t('auth.verifyEmail.title')}
      subtitle={t('auth.verifyEmail.subtitle')}
    >
      <AuthCard
        label={t('auth.verifyEmail.cardLabel')}
        labelColor="primary"
        title={t('auth.verifyEmail.cardTitle')}
        subtitle={t('auth.verifyEmail.cardSubtitle')}
        footer={{
          text: t('auth.verifyEmail.footer.text'),
          linkText: t('auth.verifyEmail.footer.linkText'),
          href: '/login',
        }}
      >
        {status === 'loading' && (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
            <p className="text-slate-600">{t('auth.verifyEmail.loading')}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-2xl border border-success/25 bg-success/10 p-4 text-success">
              <CheckCircle className="w-6 h-6 flex-shrink-0" />
              <span>{message}</span>
            </div>
            <Link to="/login">
              <Button variant="primary" className="w-full">
                {t('auth.verifyEmail.successCta')}
              </Button>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-2xl border border-error/25 bg-error/10 p-4 text-error">
              <AlertCircle className="w-6 h-6 flex-shrink-0" />
              <span>{message}</span>
            </div>
            <Link to="/register">
              <Button variant="primary" className="w-full">
                {t('auth.verifyEmail.errorCta')}
              </Button>
            </Link>
          </div>
        )}

        {status === 'idle' && null}
      </AuthCard>
    </AuthLayout>
  );
};
